import { DatasetListResponse } from "datasets/types/interfaces/datasetListItem";
import { mutate } from "swr";
import { apiMutate } from "~/utils/apiClient";

const { VITE_CATALOG_API } = import.meta.env;
const DATASETS_API_PREFIX = `${VITE_CATALOG_API}/v1/datasets`;

/** Long enough to overflow the two-line clamp every reader of `catalogDatasetsListUrl` renders. */
const LIST_DESCRIPTION_MAX_LENGTH = 300;

/**
 * The full catalog dataset list the nextgen project UI reads to resolve a
 * project's attached dataset ids to display records. Exported as a single
 * source of truth so SWR cache revalidations key-match the fetch exactly —
 * a mismatch would silently fail to refresh. Descriptions are truncated, so
 * don't search them client-side; `useDatasets` returns them in full.
 */
export const catalogDatasetsListUrl = `${DATASETS_API_PREFIX}?offset=0&limit=1000&sort=title&description_max_length=${LIST_DESCRIPTION_MAX_LENGTH}`;

const isDatasetListKey = (key: unknown) =>
  typeof key === "string" && key.startsWith(`${DATASETS_API_PREFIX}?`);

/** Revalidate every cached dataset-list key (`useDatasets`'s sort/offset variants,
 *  plus `catalogDatasetsListUrl`) — a bare per-dataset `mutate()` leaves
 *  list/card views stale after an edit since they're cached under a separate key. */
export const mutateDatasetList = () => mutate(isDatasetListKey);

/**
 * Archive (soft-delete) a dataset. Throws `ApiError` on failure — `403` when the
 * caller is not an owner, `404` when the dataset is already archived or not
 * visible to them.
 */
export const deleteDataset = async (datasetId: string): Promise<void> => {
  // The endpoint returns 204 on success
  await apiMutate(`${VITE_CATALOG_API}/v1/datasets/${datasetId}`, "DELETE", undefined, {
    expects204: true,
  });
  // Drop it from every cached list so callers can navigate straight to one, then revalidate.
  mutate(
    isDatasetListKey,
    (current?: DatasetListResponse) =>
      current && {
        ...current,
        items: current.items.filter((dataset) => dataset.id !== datasetId),
      }
  );
};
